export interface ConfigContext {
    (id: string): unknown | Promise<unknown>;
    keys(): string[];
}

export interface ConfigContextDefinition {
    path: string;
    context: ConfigContext;
}

interface ConfigLogger {
    info: (...args: any[]) => void;
    warn: (...args: any[]) => void;
    error: (...args: any[]) => void;
    critical: (...args: any[]) => void;
}

const unwrapConfigModule = (module: any): any => {
    const isModuleNamespace = module && typeof module === 'object'
        && (module.__esModule === true || module[Symbol.toStringTag] === 'Module');

    return isModuleNamespace && Object.prototype.hasOwnProperty.call(module, 'default')
        ? module.default
        : module;
};

export const createConfigLoader = (definitions: readonly ConfigContextDefinition[], logger: ConfigLogger) => {
    const contexts = new Map(definitions.map(({ path, context }) => [path, context]));
    const cache = new Map<string, Promise<any>>();

    const loadConfig = (fullPath: string): Promise<any> => {
        try {
            const pathParts = fullPath.split('/');
            const fileName = pathParts.pop();
            if (!fileName) {
                logger.warn(`Invalid path format: ${fullPath}`);
                return Promise.resolve({});
            }

            for (let length = pathParts.length; length >= 0; length--) {
                const directory = pathParts.slice(0, length).join('/');
                const context = contexts.get(directory);
                if (!context) continue;

                const adjustedPath = `./${[...pathParts.slice(length), `${fileName}.json`].join('/')}`;
                const keys = context.keys();
                let modulePath = keys.includes(adjustedPath) ? adjustedPath : undefined;

                if (!modulePath) {
                    const alternatives = keys.filter(key =>
                        key.endsWith(`/${fileName}.json`) || key === `./${fileName}.json`
                    );
                    if (alternatives.length > 0) {
                        logger.warn('Config not found at exact path, but found alternatives:', alternatives);
                        modulePath = alternatives[0];
                    }
                }

                if (!modulePath) {
                    logger.error(`Config not found: ${fullPath} (adjusted to ${adjustedPath})`);
                    return Promise.resolve({});
                }

                const cacheKey = `${directory}/${modulePath}`;
                const cached = cache.get(cacheKey);
                if (cached) {
                    logger.info(`Using cached config for ${fullPath}`);
                    return cached;
                }

                const resolvedPath = modulePath;
                const pending = Promise.resolve()
                    .then(() => context(resolvedPath))
                    .then(unwrapConfigModule)
                    .then(config => {
                        logger.info(`Loaded config for ${fullPath}:`, config);
                        return config;
                    })
                    .catch(error => {
                        cache.delete(cacheKey);
                        logger.critical(`Error loading config: ${fullPath}`, error);
                        return {};
                    });
                cache.set(cacheKey, pending);
                return pending;
            }

            logger.warn(`No context found for path: ${fullPath}`);
            return Promise.resolve({});
        } catch (error) {
            logger.critical(`Error loading config: ${fullPath}`, error);
            return Promise.resolve({});
        }
    };

    const preloadConfigs = (paths: string[]): Promise<any[]> => Promise.all(paths.map(loadConfig));

    return { loadConfig, preloadConfigs };
};
